import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createMyFam, type CreateMyFamInput } from "../lib/api";
import { resolveBankroll } from "../lib/bankroll";
import type { BankrollTier } from "../lib/types";
import { BankrollPicker } from "./BankrollPicker";

interface CreateFamModalProps {
	// Caller passes how many fams the user already has + whether their
	// subscription is active. Drives the copy that explains the remaining
	// quota; visibility is the user's free choice in every case.
	existingFamCount: number;
	isPaid: boolean;
	onClose: () => void;
	onCreated: (fam: { id: string; slug: string | null; name: string }) => void;
}

export function CreateFamModal({
	existingFamCount,
	isPaid,
	onClose,
	onCreated,
}: CreateFamModalProps) {
	const queryClient = useQueryClient();
	const [name, setName] = useState("");
	// Default to private — most people creating a Fam are doing it for their
	// own crew first. Public is the explicit "broadcast" choice.
	const [visibility, setVisibility] = useState<"public" | "private">("private");
	// Bankroll — free accounts are pinned to the $10K preset.
	const [bankrollTier, setBankrollTier] = useState<BankrollTier>("10k");
	const [customBankroll, setCustomBankroll] = useState("");
	const [error, setError] = useState<string | null>(null);

	const create = useMutation({
		mutationFn: (input: CreateMyFamInput) => createMyFam(input),
		onSuccess: (fam) => {
			void queryClient.invalidateQueries({ queryKey: ["me", "fams"] });
			onCreated(fam);
		},
	});

	// isPersonal stays on the wire as a back-compat tag — the DB column is
	// still there. We mark the FIRST Fam on an account as personal so that
	// reporting/audit can pick it apart later, but it no longer affects the
	// quota or the visibility rules.
	const isPersonal = existingFamCount === 0;

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError(null);
		const cleaned = name.trim();
		if (!cleaned) {
			setError("Give your Fam a name.");
			return;
		}
		if (cleaned.length > 50) {
			setError("Name must be 50 characters or fewer.");
			return;
		}
		const bankroll = resolveBankroll(bankrollTier, customBankroll);
		if (!bankroll.ok) {
			setError(bankroll.error);
			return;
		}
		try {
			await create.mutateAsync({
				name: cleaned,
				isPersonal,
				visibility,
				bankrollTier: bankroll.bankrollTier,
				...(bankroll.bankrollUsd !== undefined
					? { bankrollUsd: bankroll.bankrollUsd }
					: {}),
			});
		} catch (err) {
			// Surface server error inline; keep the modal open so the user
			// can correct the name (e.g., slug collision) without retyping.
			setError(err instanceof Error ? err.message : "Couldn't create Fam.");
		}
	}

	const submitting = create.isPending;
	const hint = isPaid
		? "Public Fams are visible to everyone. Private Fams are invite-only."
		: existingFamCount === 0
			? "Free tier — 1 Fam included, public or private. Upgrade to run more."
			: "Free tier — you already own your 1 included Fam. Upgrade to create more.";

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label="Create a Fam"
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
			onClick={(e) => {
				if (e.target === e.currentTarget && !submitting) onClose();
			}}
		>
			<div className="w-full max-w-md space-y-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5">
				<div className="flex items-start justify-between gap-3">
					<div>
						<h2 className="text-lg font-semibold">Create your Fam</h2>
						<p className="text-xs text-[var(--color-muted)]">
							Your Fam is your investing crew. Pick a name and visibility.
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						disabled={submitting}
						className="rounded-md border border-[var(--color-border)] px-2 py-1 text-xs hover:border-[var(--color-accent)] disabled:opacity-50"
					>
						Close
					</button>
				</div>

				<form onSubmit={onSubmit} className="space-y-4">
					<label className="block space-y-1">
						<span className="text-sm font-medium">Fam name</span>
						<input
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="e.g., Long-Only Mafia"
							maxLength={50}
							required
							autoFocus
							disabled={submitting}
							className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elev)] px-3 py-2 text-sm placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:outline-none"
						/>
					</label>

					<fieldset className="space-y-2">
						<legend className="text-sm font-medium">Visibility</legend>
						<div className="grid gap-2 sm:grid-cols-2">
							<label
								className={`flex cursor-pointer flex-col gap-1 rounded-md border p-3 text-left text-sm transition ${
									visibility === "private"
										? "border-[var(--color-accent)] bg-[var(--color-bg-elev)]"
										: "border-[var(--color-border)] hover:border-[var(--color-accent)]"
								}`}
							>
								<div className="flex items-center gap-2">
									<input
										type="radio"
										name="visibility"
										value="private"
										checked={visibility === "private"}
										onChange={() => setVisibility("private")}
										disabled={submitting}
										className="h-4 w-4 accent-[var(--color-accent)]"
									/>
									<span className="font-semibold">Private</span>
								</div>
								<span className="text-xs text-[var(--color-muted)]">
									Invite-only. You decide who joins.
								</span>
							</label>

							<label
								className={`flex cursor-pointer flex-col gap-1 rounded-md border p-3 text-left text-sm transition ${
									visibility === "public"
										? "border-[var(--color-accent)] bg-[var(--color-bg-elev)]"
										: "border-[var(--color-border)] hover:border-[var(--color-accent)]"
								}`}
							>
								<div className="flex items-center gap-2">
									<input
										type="radio"
										name="visibility"
										value="public"
										checked={visibility === "public"}
										onChange={() => setVisibility("public")}
										disabled={submitting}
										className="h-4 w-4 accent-[var(--color-accent)]"
									/>
									<span className="font-semibold">Public</span>
								</div>
								<span className="text-xs text-[var(--color-muted)]">
									Visible to everyone, invite up to 4 friends.
								</span>
							</label>
						</div>
						<p className="text-xs text-[var(--color-muted)]">{hint}</p>
					</fieldset>

					<BankrollPicker
						tier={bankrollTier}
						customUsd={customBankroll}
						onTierChange={setBankrollTier}
						onCustomChange={setCustomBankroll}
						isPaid={isPaid}
						disabled={submitting}
					/>

					{error && (
						<p className="text-sm text-[var(--color-danger)]">{error}</p>
					)}

					<div className="flex justify-end gap-2">
						<button
							type="button"
							onClick={onClose}
							disabled={submitting}
							className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)] disabled:opacity-50"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={submitting || name.trim().length === 0}
							className="rounded-md bg-[var(--color-accent)] px-4 py-1.5 text-sm font-semibold text-black hover:opacity-90 disabled:opacity-50"
						>
							{submitting ? "Creating…" : "Create Fam"}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}
